import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import Modal from "../../layouts/Modal";
import { LeadStatus } from "../../models/Lead";
import {
  IVehicleAcquisitionDrilldownItem,
  VehicleAcquisitionMetricKey,
} from "../../models/VehicleAcquisitionDrilldown";
import LoadingWheel from "../shared/LoadingWheel";
import useVehicleAcquisitionDrilldown from "../../data/useVehicleAcquisitionDrilldown";

export type DrilldownOpenTarget = "lead" | "vehicle";

interface IVehicleAcquisitionDrilldownModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  metric: VehicleAcquisitionMetricKey | null;
  metricLabel: string;
  onOpen: (row: IVehicleAcquisitionDrilldownItem, target: DrilldownOpenTarget) => void;
  leadLabel: string;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

export default function VehicleAcquisitionDrilldownModal({
  open,
  setOpen,
  metric,
  metricLabel,
  onOpen,
  leadLabel,
}: IVehicleAcquisitionDrilldownModalProps) {
  const { t } = useTranslation("dashboard");
  const drilldown = useVehicleAcquisitionDrilldown(open ? metric : null);

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-6xl">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {metricLabel || t("Vehicle Acquisition Drilldown")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("Vehicles and leads behind this metric")}
          </p>
        </div>
        <button
          type="button"
          className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          onClick={() => setOpen(false)}
        >
          {t("Close")}
        </button>
      </div>

      <div className="mt-4 max-h-[65vh] overflow-auto rounded-lg border border-gray-200">
        {drilldown.isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <LoadingWheel />
          </div>
        ) : drilldown.data?.data.rows?.length ? (
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="sticky top-0 border-b border-gray-200 bg-gray-50">
                <th className="px-3 py-2 font-semibold text-gray-700">{t("VRM")}</th>
                <th className="px-3 py-2 font-semibold text-gray-700">
                  {t("Stock #")}
                </th>
                <th className="px-3 py-2 font-semibold text-gray-700">
                  {t("Customer")}
                </th>
                <th className="px-3 py-2 font-semibold text-gray-700">{t("Phone")}</th>
                <th className="px-3 py-2 font-semibold text-gray-700">{t("Status")}</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">
                  {t("Calls")}
                </th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">
                  {t("Unread")}
                </th>
                <th className="px-3 py-2 font-semibold text-gray-700">
                  {t("Last Activity")}
                </th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">
                  {t("Open")}
                </th>
              </tr>
            </thead>
            <tbody>
              {drilldown.data.data.rows.map((row) => (
                <tr
                  key={row.leadId}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-3 py-2 font-medium text-gray-900">
                    {row.vrm || "—"}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {row.stockNumber || "—"}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {row.customerName || "—"}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {row.telephone || "—"}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {LeadStatus[row.leadStatus] != null ? t(LeadStatus[row.leadStatus]) : row.leadStatus}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-gray-900">
                    {row.callCount}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-gray-900">
                    {row.unreadMessageCount}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {formatDate(row.relevantAt)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50"
                        onClick={() => onOpen(row, "lead")}
                      >
                        {leadLabel}
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50"
                        onClick={() => onOpen(row, "vehicle")}
                      >
                        {t("Vehicle")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            {t("No vehicles found for this metric.")}
          </div>
        )}
      </div>
    </Modal>
  );
}
